import {
	ListOverridesQueryService,
	type ItemProvenance,
	type LayerRef,
	type ResolvedListConfigModel,
	type SavedSearchModel,
	type TabProvenance,
} from '@supply-mover-2-0/core';
import clsx from 'clsx';
import {
	useCallback,
	useEffect,
	useMemo,
	useState,
	type ReactElement,
} from 'react';
import { UserSelector } from '../../../components/forms/entity-selectors/UserSelector';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { tailwindClasses } from '../../../themes/tailwind-classes';
import type { ListConfiguration } from '../../types';

interface OverridePreviewProps {
	listKey: string;
	editorLists: Array<ListConfiguration>;
	savedSearches: Array<SavedSearchModel>;
}

function layerBadgeClass(ref: LayerRef | undefined): string {
	switch (ref?.kind) {
		case 'entity':
			return tailwindClasses.badgeInfo;
		case 'list':
			return tailwindClasses.badgeSuccess;
		case 'personal':
			return tailwindClasses.badgeWarning;
		default:
			return 'bg-subtle text-muted';
	}
}

/** One line of "why": which layer did what to this item. */
function describe(
	p: ItemProvenance | TabProvenance | undefined,
	t: (text: string) => string,
): Array<{ text: string; ref: LayerRef }> {
	if (p == null) {
		return [];
	}
	const out: Array<{ text: string; ref: LayerRef }> = [];
	if ('addedBy' in p && p.addedBy != null) {
		out.push({ text: t('added by'), ref: p.addedBy });
	}
	if ('removedBy' in p && p.removedBy != null) {
		out.push({ text: t('removed by'), ref: p.removedBy });
	}
	if ('hiddenBy' in p && p.hiddenBy != null) {
		out.push({ text: t('hidden by'), ref: p.hiddenBy });
	}
	if ('modifiedBy' in p && p.modifiedBy != null) {
		out.push({ text: t('edited by'), ref: p.modifiedBy });
	}
	if ('movedBy' in p && p.movedBy != null) {
		out.push({ text: t('moved by'), ref: p.movedBy });
	}
	return out;
}

function ProvenanceChips({
	items,
}: {
	items: Array<{ text: string; ref: LayerRef }>;
}): ReactElement | null {
	const { t } = useTranslation();
	if (items.length === 0) {
		return null;
	}
	return (
		<span className="flex items-center gap-1 flex-wrap">
			{items.map((item) => (
				<span
					key={`${item.text}:${item.ref.label}`}
					className={clsx(
						'px-1.5 py-0.5 text-[11px] rounded whitespace-nowrap',
						layerBadgeClass(item.ref),
					)}
				>
					{item.text} {t(item.ref.label)}
				</span>
			))}
		</span>
	);
}

/**
 * "What does this person actually see, and why?" (PD-14278). Pick a user;
 * the server resolves every layer as them and reports, per tab, column and
 * filter chip, which layer put it there or took it away.
 */
export function OverridePreview({
	listKey,
	editorLists,
	savedSearches,
}: OverridePreviewProps): ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();

	const [userId, setUserId] = useState<number | null>(null);
	const [resolved, setResolved] = useState<ResolvedListConfigModel | null>(
		null,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedListId, setSelectedListId] = useState<string | null>(null);

	const load = useCallback(async () => {
		if (userId == null) {
			setResolved(null);
			return;
		}
		setIsLoading(true);
		const result = await ListOverridesQueryService.resolvedAs(listKey, userId);
		setResolved(result.success ? result.data : null);
		setIsLoading(false);
	}, [listKey, userId]);

	useEffect(() => {
		void load();
	}, [load]);

	const nameFor = useCallback(
		(tabId: string): string => {
			if (tabId.startsWith('saved:')) {
				const id = Number.parseInt(tabId.slice(6), 10);
				return savedSearches.find((s) => s.id === id)?.name ?? tabId;
			}
			return editorLists.find((l) => l.id === tabId)?.name ?? tabId;
		},
		[editorLists, savedSearches],
	);

	const selectedList = useMemo(
		() => resolved?.lists.find((list) => list.id === selectedListId) ?? null,
		[resolved, selectedListId],
	);
	const listProvenance =
		selectedListId != null ? resolved?.provenance[selectedListId] : undefined;

	// Standard tabs the viewer does NOT see — worth listing with the reason.
	const hiddenTabs = useMemo(() => {
		if (resolved == null) {
			return [];
		}
		const shown = new Set(resolved.tabStrip.tabIds);
		return Object.entries(resolved.tabStrip.provenance)
			.filter(([id, p]) => !shown.has(id) && p.hiddenBy != null)
			.map(([id, p]) => ({ id, provenance: p }));
	}, [resolved]);

	// Columns and chips a layer removed from the selected list.
	const removedColumns = useMemo(() => {
		if (listProvenance == null || selectedList == null) {
			return [];
		}
		const present = new Set((selectedList.columns ?? []).map((c) => c.id));
		return Object.entries(listProvenance.columns).filter(
			([id, p]) => !present.has(id) && p.removedBy != null,
		);
	}, [listProvenance, selectedList]);
	const removedChips = useMemo(() => {
		if (listProvenance == null || selectedList == null) {
			return [];
		}
		const present = new Set(selectedList.activeFilters ?? []);
		return Object.entries(listProvenance.filters).filter(
			([id, p]) => !present.has(id) && p.removedBy != null,
		);
	}, [listProvenance, selectedList]);

	return (
		<div className="flex-1 flex flex-col overflow-hidden">
			<div
				className={clsx(
					'px-4 py-3 border-b flex items-center gap-3 flex-wrap',
					colors.classes.borderDefault,
					colors.classes.bgSecondary,
				)}
			>
				<span
					className={clsx('text-sm font-semibold', colors.classes.textPrimary)}
				>
					{t('Preview')}
				</span>
				<span className={clsx('text-xs', colors.classes.textSecondary)}>
					{t(
						'See exactly what a person gets, and which layer decided each part.',
					)}
				</span>
				<div className="ml-auto w-72">
					<UserSelector
						value={userId}
						onChange={(value) => {
							setUserId(value);
							setSelectedListId(null);
						}}
						placeholder={t('Pick a user to preview as')}
						allowClear
					/>
				</div>
			</div>

			<div className="flex-1 overflow-y-auto p-4 space-y-5">
				{userId == null ? (
					<p className={clsx('text-sm', colors.classes.textSecondary)}>
						{t('Pick a user above.')}
					</p>
				) : isLoading || resolved == null ? (
					<span className={clsx('text-sm', colors.classes.textMuted)}>
						{isLoading ? t('Resolving...') : t('Could not resolve this user.')}
					</span>
				) : (
					<>
						<div className={clsx('text-xs', colors.classes.textSecondary)}>
							{t('Roles')}:{' '}
							{resolved.viewer.roleNames.length > 0
								? resolved.viewer.roleNames.map((name) => t(name)).join(', ')
								: t('none')}
							{' · '}
							{t('Layers')}:{' '}
							{resolved.tabStrip.layers
								.map((layer) => t(layer.label))
								.join(' → ')}
						</div>

						{/* Tab strip */}
						<section>
							<h4
								className={clsx(
									'text-xs font-semibold uppercase tracking-wider mb-2',
									colors.classes.textMuted,
								)}
							>
								{t('Tabs, in order')}
							</h4>
							<div className="flex flex-wrap gap-2">
								{resolved.tabStrip.tabIds.map((tabId) => (
									<button
										key={tabId}
										type="button"
										data-preview-tab={tabId}
										onClick={() => {
											if (!tabId.startsWith('saved:')) {
												setSelectedListId(tabId);
											}
										}}
										className={clsx(
											'px-2 py-1 rounded border text-sm flex items-center gap-2',
											colors.classes.borderDefault,
											tabId === selectedListId
												? 'bg-primary-50 dark:bg-surface'
												: colors.classes.bgPrimary,
											colors.classes.textPrimary,
										)}
									>
										{t(nameFor(tabId))}
										<ProvenanceChips
											items={describe(resolved.tabStrip.provenance[tabId], t)}
										/>
									</button>
								))}
							</div>
							{hiddenTabs.length > 0 && (
								<div className="mt-2 flex flex-wrap gap-2 items-center">
									<span className={clsx('text-xs', colors.classes.textMuted)}>
										{t('Not shown')}:
									</span>
									{hiddenTabs.map(({ id, provenance }) => (
										<span
											key={id}
											className={clsx(
												'text-xs line-through flex items-center gap-1',
												colors.classes.textSecondary,
											)}
										>
											{t(nameFor(id))}
											<ProvenanceChips items={describe(provenance, t)} />
										</span>
									))}
								</div>
							)}
						</section>

						{/* Selected list */}
						{selectedList != null && listProvenance != null && (
							<section className="space-y-4">
								<h4
									className={clsx(
										'text-xs font-semibold uppercase tracking-wider',
										colors.classes.textMuted,
									)}
								>
									{t(selectedList.name ?? selectedList.id)} · {t('Layers')}:{' '}
									{listProvenance.layers
										.map((layer) => t(layer.label))
										.join(' → ')}
								</h4>

								<div>
									<div
										className={clsx(
											'text-xs font-medium mb-1',
											colors.classes.textSecondary,
										)}
									>
										{t('Columns')}
									</div>
									<ol className="space-y-1">
										{(selectedList.columns ?? []).map((column, index) => (
											<li
												key={column.id}
												className={clsx(
													'text-sm flex items-center gap-2',
													colors.classes.textPrimary,
												)}
											>
												<span
													className={clsx(
														'w-5 text-right text-xs',
														colors.classes.textMuted,
													)}
												>
													{String(index + 1)}
												</span>
												<span>
													{t(
														column.label ??
															column.headers?.[0]?.text ??
															column.id,
													)}
												</span>
												<ProvenanceChips
													items={describe(listProvenance.columns[column.id], t)}
												/>
											</li>
										))}
										{removedColumns.map(([id, p]) => (
											<li
												key={id}
												className={clsx(
													'text-sm flex items-center gap-2 line-through',
													colors.classes.textSecondary,
												)}
											>
												<span className="w-5" />
												<span>{t(id)}</span>
												<ProvenanceChips items={describe(p, t)} />
											</li>
										))}
									</ol>
								</div>

								<div>
									<div
										className={clsx(
											'text-xs font-medium mb-1',
											colors.classes.textSecondary,
										)}
									>
										{t('Filter chips')}
									</div>
									<div className="flex flex-wrap gap-2">
										{(selectedList.activeFilters ?? []).map((chipId) => (
											<span
												key={chipId}
												className={clsx(
													'px-2 py-0.5 rounded border text-xs flex items-center gap-1',
													colors.classes.borderDefault,
													colors.classes.textPrimary,
												)}
											>
												{t(chipId)}
												<ProvenanceChips
													items={describe(listProvenance.filters[chipId], t)}
												/>
											</span>
										))}
										{removedChips.map(([id, p]) => (
											<span
												key={id}
												className={clsx(
													'px-2 py-0.5 text-xs line-through flex items-center gap-1',
													colors.classes.textSecondary,
												)}
											>
												{t(id)}
												<ProvenanceChips items={describe(p, t)} />
											</span>
										))}
									</div>
								</div>

								{Object.keys(listProvenance.filterRules).length > 0 && (
									<div>
										<div
											className={clsx(
												'text-xs font-medium mb-1',
												colors.classes.textSecondary,
											)}
										>
											{t('Filter rules changed')}
										</div>
										<div className="flex flex-wrap gap-2">
											{Object.entries(listProvenance.filterRules).map(
												([id, ref]) => (
													<span
														key={id}
														className={clsx(
															'px-2 py-0.5 rounded text-xs',
															layerBadgeClass(ref),
														)}
													>
														{t(id)} · {t(ref.label)}
													</span>
												),
											)}
										</div>
									</div>
								)}
							</section>
						)}
						{selectedList == null && (
							<p className={clsx('text-sm', colors.classes.textSecondary)}>
								{t('Click a tab above to see its columns and filter chips.')}
							</p>
						)}
					</>
				)}
			</div>
		</div>
	);
}
